/**
 * The expanded-form calculator's word form for the locales with a real spelled-out
 * number module (the German, French, Spanish and Chinese number converters). Every other
 * locale returns `null`: the English assembler (`numberInWords`) is wrong for them, so the
 * widget shows no word form rather than an English-grammar one.
 *
 * Decimals are read digit by digit ("tres coma uno cuatro"); the fraction-name style
 * ("fifty-seven hundredths") is an English-only reading.
 */

import { type Decimal, trimDecimal } from "@/lib/tool-kit/arithmetic/decimal";
import { chineseSpell } from "@/lib/tool-kit/numbers-languages/chinese";
import { frenchNumber } from "@/lib/tool-kit/numbers-languages/french";
import { germanNumber } from "@/lib/tool-kit/numbers-languages/german";
import {
  MAX_FRACTION_DIGITS,
  MAX_INTEGER_DIGITS,
} from "@/lib/tool-kit/numbers-languages/number-input";
import { spanishNumber } from "@/lib/tool-kit/numbers-languages/spanish";

/** Whether the word form has English's "and" and fraction-name options. */
export function hasWordFormOptions(locale: string): boolean {
  return locale === "en";
}

/**
 * The number in words for a non-English locale, or `null` when the locale has no word
 * form or the number is beyond the module's range (24 integer digits, 20 decimals).
 */
export function localizedWordForm(
  value: Decimal,
  locale: string
): string | null {
  const trimmed = trimDecimal(value);
  if (
    trimmed.int.length > MAX_INTEGER_DIGITS ||
    trimmed.frac.length > MAX_FRACTION_DIGITS
  ) {
    return null;
  }
  const negative = trimmed.negative;
  switch (locale) {
    case "de":
      return germanNumber(negative, trimmed.int, trimmed.frac, {
        one: "eins",
        einPrefix: false,
        swiss: false,
      }).words;
    case "fr":
      return frenchNumber(negative, trimmed.int, trimmed.frac, {
        region: "france",
        hyphenation: "traditional",
        gender: "masculine",
      }).words;
    case "es":
      return spanishNumber(negative, trimmed.int, trimmed.frac, {
        noun: "none",
        femBeforeMil: "apocope",
        mark: "coma",
      }).words;
    case "zh":
      return chineseSpell(negative, trimmed.int, trimmed.frac, {
        script: "simplified",
        style: "standard",
        form: "full",
        liang: false,
        units: "compound",
        circleZero: false,
      }).words;
    default:
      return null;
  }
}
